import { ArrowUpRight } from "lucide-react";
import { actorName } from "../../lib/people";
import type { ReleaseEventView } from "../../types";
import { TimeAgo } from "../time-ago";

const LABELS: Record<string, string> = {
  draft_created: "Draft release created in GitHub",
  draft_created_in_github: "Draft created by hand in GitHub",
  branch_created: "Hotfix branch cut from the last release",
  issue_added: "Issue added",
  issue_removed: "Issue removed",
  issue_dropped: "Issue dropped",
  ship_requested: "Ship requested",
  merges_released: "Held pull requests released to the Merge agent",
  published: "Published: tag created, deploy triggered",
  published_in_github: "Published in GitHub",
  synced: "Hotfix branch merged back into main",
  sync_conflict: "Hotfix branch conflicts with main",
  deploy_started: "Deploy workflow started",
  deployed: "Deployed",
  deploy_failed: "Deploy workflow failed",
  version_advanced: "Moved to the next version",
  edited_in_github: "Edited in GitHub",
  deleted_in_github: "Deleted in GitHub",
  cancelled: "Cancelled",
  retry: "Publish retried",
  notices_resent: "Slack summary resent to teams",
  notes_failed: "GitHub's release notes are missing",
  notes_regenerated: "Release notes regenerated",
  finished: "Finished shipping",
  error: "Error",
};

const str = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

/** Who an event attributes, preferring the name recorded when the button was pressed. */
export function releaseEventActor(event: ReleaseEventView | undefined): string | null {
  if (!event) return null;
  return str(event.payload.byName) || actorName(str(event.payload.by));
}

export function ReleaseTimelineItem({ event }: { event: ReleaseEventView }) {
  const p = event.payload;
  const label = LABELS[event.type] ?? event.type.replace(/_/g, " ");
  const detail = [
    str(p.identifier),
    str(p.tag) && event.type !== "issue_added" ? str(p.tag) : undefined,
    str(p.branch),
    str(p.workflow),
    str(p.reason),
    str(p.error),
    Array.isArray(p.issues) ? (p.issues as string[]).join(", ") : undefined,
    Array.isArray(p.teams) ? (p.teams as string[]).join(", ") : undefined,
    Array.isArray(p.fields) ? `changed ${(p.fields as string[]).join(", ")}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
  const by = releaseEventActor(event);
  const url = str(p.url);
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className="font-medium">{label}</span>
      {detail && <span className="text-muted-foreground">{detail}</span>}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 text-primary hover:underline"
        >
          open <ArrowUpRight className="size-3" />
        </a>
      )}
      <span className="ml-auto text-xs text-muted-foreground tabular-nums">
        {actorName(by) ? `${actorName(by)} · ` : ""}
        <TimeAgo at={event.createdAt} />
      </span>
    </li>
  );
}
